import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CreatorAppProps, EventFormData } from './types';
import { useFormStore } from './stores/formStore';
import { FormProvider } from './contexts/FormContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ApiProvider as LocalApiProvider } from './contexts/ApiContext';
import { ApiProvider as ReactUIApiProvider, BalloonEventsMetadataProvider, UserProvider, useUser, useBalloonEventsMetadata, useApi } from 'react-ui';
import { transformBalloonEventToFormData } from './transformers';
import { BalloonEventModel, CategoryModel, configureApiFetch, getBackendConfig } from 'ts-core';
import { CreateEventForm } from './components/CreateEventForm';
import { Modal } from './components/common/Modal';
import { reverseGeocodeLocation, normalizeLocation } from './services/mapbox';
import { getDraftService } from './services/draftService';
import { getBalloonService } from './services/balloonService';
import './styles/globals.css';

/**
 * Simple Creator App - Main component for event creation
 * 
 * @example
 * ```tsx
 * // Create mode
 * <CreatorApp
 *   mode="create"
 *   onSuccess={(event) => console.log('Created:', event)}
 *   locale="en"
 * />
 * 
 * // Edit mode
 * <CreatorApp
 *   mode="edit"
 *   event={existingEvent}
 *   onSuccess={(event) => console.log('Updated:', event)}
 * />
 * ```
 */
export const CreatorApp: React.FC<CreatorAppProps> = ({
  mode = 'create',
  event,
  editIdentifier,
  cloneEvent,
  initialData,
  apiEndpoint,
  apiKey,
  locale = 'en',
  theme = {},
  features = {},
  debug = false,
  className,
  modal = false,
  autoSave = true,
  autoSaveInterval = 5000,
  draftId,
  geoLocation,
  skipProviders = false,
  onSuccess,
  onCancel,
  onValidationError,
  onDraftSave,
  onError
}) => {
  const [isReady, setIsReady] = useState(false);
  const { initializeForm, resetForm, updateField } = useFormStore();

  // Bootstrap apiFetch global config — only set apiKey here.
  // partnerKey is set by the host app (map/site) and must not be overwritten.
  useEffect(() => {
    const key = apiKey || import.meta.env.VITE_API_KEY || '';
    try {
      const backendConfig = getBackendConfig();
      configureApiFetch({ apiKey: backendConfig.apiKey || key });
    } catch {
      configureApiFetch({ apiKey: key });
    }
  }, [apiKey]);

  // Validate props - event is optional if editIdentifier is provided (will be loaded)
  useEffect(() => {
    if (mode === 'edit' && !event && !editIdentifier) {
      const error = new Error('Event or editIdentifier prop is required when mode is "edit"');
      onError?.(error);
      console.error(error);
      return;
    }
    setTimeout(() => {
        setIsReady(true);
    }, 10)
  }, [mode, event, editIdentifier, onError]);

  // Initialize form data
  useEffect(() => {
    if (!isReady) return;

    if (mode === 'edit' && event) {
      // Transform BalloonEventModel to form data
      const formData = transformModelToFormData(event);
      initializeForm(formData);
    } else if (mode === 'create' && cloneEvent) {
      // Clone mode: pre-fill form with source event data but no editId
      if (debug) {
        console.log('[MediaFlow] Clone init — source images:', cloneEvent._images?.length ?? 0);
      }
      const formData = transformBalloonEventToFormData(cloneEvent, { isDraft: false });
      formData.editId = undefined;
      formData.isDraft = false;
      formData.draftId = undefined;
      formData.isClone = true;
      initializeForm(formData);
    } else if (initialData) {
      // Pre-filled data (e.g. from AI extraction) — use it instead of an empty form
      initializeForm(initialData);
    } else {
      // Start with empty form (draft loading handled by DraftLoader component)
      resetForm();
    }
  }, [isReady, mode, event, cloneEvent, initialData, initializeForm, resetForm]);

  // Handle geoLocation prop - auto-fill location from coordinates
  // Skip if we're loading a draft (draftId is set)
  useEffect(() => {
    if (!geoLocation || !isReady || draftId) return;
    
    const fillLocationFromCoordinates = async () => {
      try {
        const location = await reverseGeocodeLocation(geoLocation);
        
        // Update location fields in the form (don't mark dirty - this is initial data)
        updateField('location', location, false);
        
        if (debug) {
          console.log('Location auto-filled from coordinates:', location);
        }
      } catch (error) {
        console.error('Failed to reverse geocode location:', error);
        // Still set the coordinates even if geocoding fails (don't mark dirty)
        updateField('location', {
          name: 'Event Location',
          address: `${geoLocation.lat.toFixed(6)}, ${geoLocation.lng.toFixed(6)}`,
          coordinates: {
            lat: geoLocation.lat,
            lng: geoLocation.lng
          }
        }, false);
      }
    };
    
    fillLocationFromCoordinates();
  }, [geoLocation, isReady, draftId, updateField, debug]);

  // Memoize configuration
  const config = useMemo(() => ({
    api: {
      endpoint: apiEndpoint || import.meta.env.VITE_API_ENDPOINT || 'https://beat.balloon-events.com',
      key: apiKey || import.meta.env.VITE_API_KEY || ''
    },
    theme: {
      primaryColor: theme.primaryColor || '#BF2088',
      fontFamily: theme.fontFamily || 'Syne, sans-serif',
      borderRadius: theme.borderRadius || 'md',
      darkMode: theme.darkMode || false
    },
    features: {
      naturalLanguageDates: features.naturalLanguageDates ?? true,
      duplicateDetection: features.duplicateDetection ?? true,
      templates: features.templates ?? true,
      recurringEvents: features.recurringEvents ?? false,
      aiAssist: features.aiAssist ?? false,
      imageUpload: features.imageUpload ?? true,
      richTextEditor: features.richTextEditor ?? false,
      mapPicker: features.mapPicker ?? true
    },
    locale,
    debug,
    autoSave,
    autoSaveInterval
  }), [apiEndpoint, apiKey, theme, features, locale, debug, autoSave, autoSaveInterval]);

  // Handle callbacks
  const callbacks = useMemo(() => ({
    onSuccess: onSuccess || (() => {}),
    onCancel: onCancel || (() => {}),
    onValidationError: onValidationError || (() => {}),
    onDraftSave: onDraftSave || (() => {}),
    onError: onError || ((error: Error) => console.error(error)),
    // onPublish is called by CreateEventForm after successful publish
    // Map it to onSuccess for backwards compatibility
    onPublish: onSuccess ? (eventId: string) => onSuccess({ _id: eventId } as any) : (() => {}),
  }), [onSuccess, onCancel, onValidationError, onDraftSave, onError]);

  if (!isReady) {
    return (
      <div className="loading">
        <div className="loading-spinner" />
        <span>Initializing...</span>
      </div>
    );
  }

  // Inner content without react-ui providers (when skipProviders=true, parent provides them)
  const innerContent = (
    <LocalApiProvider config={config.api}>
      <ThemeProvider theme={config.theme}>
        <FormProvider 
          mode={mode}
          config={config}
          callbacks={callbacks}
        >
          <DraftLoader draftId={draftId} apiEndpoint={config.api.endpoint} apiKey={config.api.key}>
            <BalloonLoader 
              editIdentifier={editIdentifier} 
              editEvent={event}
              mode={mode}
            >
              <CreateEventForm />
            </BalloonLoader>
          </DraftLoader>
        </FormProvider>
      </ThemeProvider>
    </LocalApiProvider>
  );

  // When skipProviders is true, assume parent already provides ReactUIApiProvider, MetadataProvider, UserProvider
  const content = skipProviders ? innerContent : (
    <ReactUIApiProvider hostName={config.api.endpoint} apiKey={config.api.key}>
      <BalloonEventsMetadataProvider legacyAPI={false} blockWhileLoading={false}>
        <UserProvider partnerKey={config.api.key}>
          {innerContent}
        </UserProvider>
      </BalloonEventsMetadataProvider>
    </ReactUIApiProvider>
  );

  if (modal) {
    return (
      <Modal
        isOpen={true}
        onClose={() => callbacks.onCancel()}
        className={className}
      >
        {content}
      </Modal>
    );
  }

  return (
    <div className={`creator-app flex flex-1 ${className || ''}`}>
      {content}
    </div>
  );
};

/**
 * Transform BalloonEventModel to form data for editing
 * Uses the unified transformer from transformers module
 */
function transformModelToFormData(event: BalloonEventModel): EventFormData {
  return transformBalloonEventToFormData(event, { isDraft: false });
}

/**
 * BalloonLoader component - loads existing balloon from API for editing
 */
interface BalloonLoaderProps {
  editIdentifier?: string;
  editEvent?: BalloonEventModel;
  mode: 'create' | 'edit';
  children: React.ReactNode;
}

const BalloonLoader: React.FC<BalloonLoaderProps> = ({ editIdentifier, editEvent, mode, children }) => {
  const { apiEndpoints, apiKey } = useApi();
  const { metadata } = useBalloonEventsMetadata();
  const { initializeForm } = useFormStore();
  const [loading, setLoading] = useState(false);
  const [loadedIdentifier, setLoadedIdentifier] = useState<string | null>(null);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const initializedFromEventRef = useRef(false);

  useEffect(() => {
    // Skip if not edit mode or no identifier
    if (mode !== 'edit' || !editIdentifier) return;
    
    // If event is already provided, use it directly (once only)
    if (editEvent) {
      // Guard: prevent re-initialization when deps change (e.g. metadata loading),
      // which would wipe user edits made after the initial load.
      if (initializedFromEventRef.current) return;

      // Check edit permission
      if (editEvent.canEdit === false) {
        setPermissionError('You are not permitted to edit this balloon');
        return;
      }
      initializedFromEventRef.current = true;
      const formData = transformModelToFormData(editEvent);
      // Normalize location data for consistent address format (async)
      normalizeAndInitialize(formData).then(() => {
        console.log('[BalloonLoader] Using provided event:', editEvent._id || editEvent.id);
      });
      return;
    }

    // Skip if already loaded this identifier
    if (loadedIdentifier === editIdentifier) return;

    // Skip if metadata or apiKey not ready
    if (!metadata || !apiKey) return;

    const loadBalloonFromApi = async () => {
      setLoading(true);
      try {
        const service = getBalloonService({
          apiEndpoints,
          apiKey: apiKey,
          metadata,
        });
        const balloon = await service.getBalloon(editIdentifier);
        if (balloon) {
          // Check edit permission
          if (balloon.canEdit === false) {
            setPermissionError('You are not permitted to edit this balloon');
            return;
          }
          const formData = transformModelToFormData(balloon);
          // Normalize location data for consistent address format
          await normalizeAndInitialize(formData);
          console.log('[BalloonLoader] Balloon loaded:', editIdentifier);
        } else {
          console.error('[BalloonLoader] Balloon not found:', editIdentifier);
        }
      } catch (error) {
        console.error('[BalloonLoader] Failed to load balloon:', error);
      } finally {
        setLoading(false);
        setLoadedIdentifier(editIdentifier);
      }
    };

    loadBalloonFromApi();
  }, [editIdentifier, editEvent, mode, apiEndpoints, apiKey, metadata, initializeForm, loadedIdentifier]);

  /**
   * Normalize location and initialize form
   * Ensures consistent address format (country as "Austria" not "AT" or "Österreich")
   */
  const normalizeAndInitialize = async (formData: EventFormData) => {
    try {
      const normalizedLocation = await normalizeLocation({
        name: formData.location.name,
        address: formData.location.address,
        coordinates: formData.location.coordinates,
        addressComponents: formData.location.addressComponents,
      });

      if (normalizedLocation) {
        console.log('[BalloonLoader] Location normalized:', normalizedLocation);
        formData.location = {
          ...formData.location,
          name: normalizedLocation.name,
          address: normalizedLocation.address,
          coordinates: normalizedLocation.coordinates,
          addressComponents: normalizedLocation.addressComponents,
        };
      }
    } catch (error) {
      console.warn('[BalloonLoader] Failed to normalize location:', error);
      // Continue with original data if normalization fails
    }

    initializeForm(formData);
  };

  if (loading) {
    return (
      <div className="w-full flex-1 flex items-center justify-center p-8">
        <div className="loading-spinner" />
        <span className="ml-2">Loading event...</span>
      </div>
    );
  }

  if (permissionError) {
    return (
      <div className="w-full flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="text-red-500 text-xl mb-2">⚠️</div>
        <p className="text-red-600 font-medium">{permissionError}</p>
      </div>
    );
  }

  return <>{children}</>;
};

/**
 * DraftLoader component - loads draft from API when user is authenticated
 */
interface DraftLoaderProps {
  draftId?: string;
  apiEndpoint: string;
  apiKey: string;
  children: React.ReactNode;
}

const DraftLoader: React.FC<DraftLoaderProps> = ({ draftId, apiEndpoint, apiKey, children }) => {
  const { user } = useUser();
  const token = user?.token;
  const { metadata } = useBalloonEventsMetadata();
  const { initializeForm, setDraftId } = useFormStore();
  const [loading, setLoading] = useState(false);
  const [loadedDraftId, setLoadedDraftId] = useState<string | null>(null);

  // Category resolver function that uses metadata to resolve category slugs
  const categoryResolver = (slug: string): CategoryModel | null => {
    if (!metadata || !slug) return null;
    
    const searchInCategories = (categories: CategoryModel[]): CategoryModel | null => {
      if (!categories) return null;
      for (const category of categories) {
        if (!category || !category.slug) continue;
        if (category.slug.toLowerCase() === slug.toLowerCase()) {
          return category;
        }
        if (category.children && category.children.length > 0) {
          const found = searchInCategories(category.children);
          if (found) return found;
        }
      }
      return null;
    };
    
    const allCategories = metadata.getAllCategories();
    return allCategories ? searchInCategories(allCategories) : null;
  };

  useEffect(() => {
    // Skip if no draftId, no auth, or already loaded this specific draft
    if (!draftId || !token || !user || loadedDraftId === draftId) return;

    const loadDraftFromApi = async () => {
      setLoading(true);
      try {
        const service = getDraftService(apiEndpoint, apiKey);
        // Load draft as EventFormData (includes IndexedDB video restore)
        const formData = await service.getDraft(token, draftId, categoryResolver);
        if (formData) {
          initializeForm(formData);
          setDraftId(draftId);
        }
      } catch (error) {
        console.error('[DraftLoader] Failed to load draft:', error);
      } finally {
        setLoading(false);
        setLoadedDraftId(draftId);
      }
    };

    loadDraftFromApi();
  }, [draftId, token, user, apiEndpoint, apiKey, initializeForm, setDraftId, loadedDraftId, metadata]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="loading-spinner" />
        <span className="ml-2">Loading draft...</span>
      </div>
    );
  }

  return <>{children}</>;
};
